import { formatMoney, isElementInViewport, mediaQueryLarge } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

class StickyAddToCart extends HTMLElement {
  constructor() {
    super();

    this.form = null;
    this.footer = null;
    this.anchor = null;
    this.frame = null;
    this.selectionConfirmed = false;

    this.handleViewportChange = this.#scheduleUpdate.bind(this);
    this.handleVariantSelected = this.#handleVariantSelected.bind(this);
    this.handleVariantUpdated = this.#handleVariantUpdated.bind(this);
  }

  connectedCallback() {
    this.form = this.closest('[data-ref="product-form"]');
    this.footer = document.querySelector('[data-ref="footer"], [data-ref="seraf-footer"], footer');
    this.anchor = this.closest('[data-sticky-add-to-cart-anchor]');
    this.selectionConfirmed = this.dataset.selectionConfirmed === 'true';

    window.addEventListener('scroll', this.handleViewportChange, { passive: true });
    window.addEventListener('resize', this.handleViewportChange);
    mediaQueryLarge.addEventListener('change', this.handleViewportChange);
    this.form?.addEventListener(ThemeEvents.variantSelected, this.handleVariantSelected);
    this.form?.addEventListener(ThemeEvents.variantUpdated, this.handleVariantUpdated);

    requestAnimationFrame(() => {
      this.#syncAnchorHeight();
      this.#displayStickyAddToCart();
    });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.handleViewportChange);
    window.removeEventListener('resize', this.handleViewportChange);
    mediaQueryLarge.removeEventListener('change', this.handleViewportChange);
    this.form?.removeEventListener(ThemeEvents.variantSelected, this.handleVariantSelected);
    this.form?.removeEventListener(ThemeEvents.variantUpdated, this.handleVariantUpdated);

    if (this.frame != null) cancelAnimationFrame(this.frame);
  }

  #handleVariantSelected() {
    this.selectionConfirmed = false;
    this.dataset.selectionConfirmed = 'false';
    this.#setActive(false);
  }

  #handleVariantUpdated(event) {
    const variant = event.detail?.resource;

    if (!variant) {
      this.selectionConfirmed = false;
      this.dataset.selectionConfirmed = 'false';
      this.#setActive(false);
      return;
    }

    this.selectionConfirmed = true;
    this.dataset.selectionConfirmed = 'true';

    const sizeIndex = Number.parseInt(this.dataset.sizeOptionIndex, 10);
    const selectedSize = Number.isInteger(sizeIndex) ? variant.options?.[sizeIndex] : variant.public_title || variant.title;
    const sizeElement = this.querySelector('[data-sticky-selected-size]');
    const priceElement = this.querySelector('[data-sticky-variant-price]');

    if (sizeElement && selectedSize) sizeElement.textContent = selectedSize;
    if (priceElement && Number.isFinite(variant.price)) priceElement.innerHTML = formatMoney(variant.price);

    this.#scheduleUpdate();
  }

  #scheduleUpdate() {
    if (this.frame != null) return;

    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.#displayStickyAddToCart();
    });
  }

  #syncAnchorHeight() {
    if (!this.anchor || this.dataset.active === 'true') return;

    this.anchor.style.setProperty('--sticky-add-to-cart-anchor-height', `${this.offsetHeight}px`);
  }

  #setActive(active) {
    this.dataset.active = active ? 'true' : 'false';
    this.classList.toggle('color-scheme-1', active);

    if (!active) requestAnimationFrame(() => this.#syncAnchorHeight());
  }

  #displayStickyAddToCart() {
    const button = this.querySelector('[data-ref="add-to-cart-button"]');
    const anchorRect = this.anchor?.getBoundingClientRect();
    const mainButtonHasPassed = anchorRect ? anchorRect.bottom <= 0 : false;
    const canPurchase = Boolean(button && !button.disabled);
    const canDisplay =
      !mediaQueryLarge.matches &&
      this.selectionConfirmed &&
      canPurchase &&
      mainButtonHasPassed &&
      !isElementInViewport(this.footer);

    this.#setActive(canDisplay);
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
